import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { styled } from "@mui/material/styles";
import { TypographyLarge, TypographySmall } from "./customComponent";
import { useSettings, getSettings } from "../modules/SettingsContext";
import { logger } from "../helpers/logger";
import parser from "html-react-parser";

const StyledBox = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  ":last-child": {
    marginBottom: 0,
  },
}));

const QuickSettings = ({ open, handleClose }) => {
  const { t: tCMS } = useTranslation("cms");
  const { t: tCommon } = useTranslation();

  const { settings, updateSettings } = useSettings();
  const [savedSettings, setSavedSettings] = useState({});

  // Asset Section
  const [duplicatePlacement, setDuplicatePlacement] = useState("direct");

  useEffect(() => {
    if (open) {
      const savedSettings = getSettings();
      logger.log(`QuickSettings opened!`, { settings: savedSettings });
      setSavedSettings(savedSettings);

      if (savedSettings.assetDuplicate) {
        setDuplicatePlacement(
          savedSettings.assetDuplicate.placement || "direct"
        );
      } else {
        handleChangeDuplicatePlacement();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleCloseDialog = () => {
    handleClose();
  };

  const handleChangeDuplicatePlacement = (value = "direct") => {
    const updated = { ...savedSettings };
    if (updated.assetDuplicate) {
      updated.assetDuplicate.placement = value;
    } else {
      updated.assetDuplicate = { placement: "direct" };
    }

    setDuplicatePlacement(value);
    setSavedSettings(updated);
  };

  const submitChanges = () => {
    const updated = { ...settings, ...savedSettings };
    updateSettings(updated);
    handleCloseDialog();
  };

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      fullWidth
      disableEscapeKeyDown={true}
      onClose={() => handleCloseDialog()}
    >
      {/* TODO: Change to "Do you want to save your changes?" */}
      {/* <CloseConfirmation
        open={openCloseConfirmation}
        closeDialog={closeCloseConfirmation}
        handleConfirm={handleCloseConfirmation}
      /> */}

      <DialogTitle
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        {tCommon("quickSettings.title")}
        <IconButton onClick={() => handleCloseDialog()}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ minHeight: "33vh" }}>
        <Box
          display={"flex"}
          flexDirection={"column"}
          alignItems={"center"}
          sx={{ mt: 1, px: 2 }}
        >
          <Box display={"flex"} alignItems={"center"} sx={{ width: "100%" }}>
            <Box
              display={"flex"}
              justifyContent={"left"}
              alignItems={"start"}
              sx={{
                width: "100%",
                height: "33vh",
                border: "1px solid black",
                borderRadius: "22px",
              }}
            >
              <Box
                width={"20%"}
                height={"100%"}
                display={"flex"}
                flexDirection={"column"}
                sx={{
                  borderRight: "1px solid black",
                }}
              >
                <Button
                  sx={{
                    py: 2,
                    borderBottom: "1px solid black",
                  }}
                >
                  <Box>
                    <TypographyLarge>
                      {tCommon("quickSettings.cmsTabLabel")}
                    </TypographyLarge>
                  </Box>
                </Button>
              </Box>

              <Box
                width={"80%"}
                display={"flex"}
                flexDirection={"column"}
                gap={1}
                sx={{
                  py: 2,
                  pl: 2,
                  pr: 4,
                }}
              >
                <StyledBox>
                  <Box display={"flex"} gap={"3px"} alignItems={"center"}>
                    <TypographyLarge sx={{ ml: 1 }}>
                      {tCommon("quickSettings.duplicatePlacementLabel")}
                    </TypographyLarge>
                    <Tooltip
                      arrow
                      disableInteractive
                      placement="top"
                      title={
                        <TypographySmall
                          sx={{ color: "white", textAlign: "center" }}
                        >
                          {tCommon("quickSettings.duplicatePlacementTip")}
                        </TypographySmall>
                      }
                    >
                      <InfoOutlinedIcon fontSize="10px" />
                    </Tooltip>
                  </Box>

                  <Box display={"flex"} flexDirection={"column"} sx={{ pl: 2 }}>
                    <Box width={"100%"} display={"flex"}>
                      <TextField
                        margin="dense"
                        fullWidth
                        variant="outlined"
                        size="small"
                        select
                        SelectProps={{
                          native: true,
                        }}
                        value={duplicatePlacement}
                        onChange={(e) =>
                          handleChangeDuplicatePlacement(e.target.value)
                        }
                        sx={{ my: 1, width: "260px" }}
                      >
                        <option value={"direct"} key={"direct"}>
                          {tCMS("duplicateDialog.optionPlaceDirect")}
                        </option>
                        <option value={"top"} key={"top"}>
                          {tCMS("duplicateDialog.optionPlaceTop")}
                        </option>
                        <option value={"bottom"} key={"bottom"}>
                          {tCMS("duplicateDialog.optionPlaceBottom")}
                        </option>
                      </TextField>
                    </Box>

                    <TypographySmall flex={"none"}>
                      {parser(
                        tCMS(
                          `duplicateDialog.${duplicatePlacement}OptionPlaceTip`
                        )
                      )}
                    </TypographySmall>
                  </Box>
                </StyledBox>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ mx: 2, mb: 1 }}>
        <>
          <Button variant="outlined" onClick={() => handleCloseDialog()}>
            {tCommon("cancelBtn")}
          </Button>
          <Button variant="contained" onClick={() => submitChanges()}>
            {tCommon("applyBtn")}
          </Button>
        </>
      </DialogActions>
    </Dialog>
  );
};

export default QuickSettings;
